import React, { useState, useEffect } from "react";
import { FormattedMessage, useIntl, defineMessages } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { TextField, makeStyles, Grid } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import { Caption } from "../workOrderDetails";
import { getLocalizedNameWithIntl } from "../../../globals/langHelpers";
import { useEntryStyle } from "./RelatedWork";
import { requestActivityQuestions } from "../../../redux/activityQuestions/activityQuestionsActions";
import FollowupActivityQuestions from "./FollowupActivityQuestions";
import useActivityQuestions from "../../activityQuestions/hooks/useActivityQuestions";
import useCheckDuplicateWorkOrder from "../../../hooks/useCheckDuplicateWorkOrder";
import useIsEndToEndEnabled from "../../../hooks/useIsEndToEndEnabled";

const labels = defineMessages({
    close: {
        id: "admin.work-order-details.close",
        defaultMessage: "Close",
        description: "Admin work order close text",
    },
    open: {
        id: "admin.work-order-details.open",
        defaultMessage: "Open",
        description: "Admin work order open",
    },
    noOptions: {
        id: "admin.work-order-details.no-options",
        defaultMessage: "No options",
        description: "Admin work order no options",
    },
});

const useStyles = makeStyles((theme) => ({
    numberInput: {
        "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
            "-webkit-appearance": "none",
            display: "none",
        },
    },
    characterCount: {
        float: "right",
        marginTop: "-20px",
        marginRight: "15px",
        fontSize: "10px",
        color: "#999999",
    },
    gridItem: {
        marginBottom: "1em",
    },
}));

const FollowUpsRowEdit = (props) => {
    const { reasons, isDisabled, followUp, handleFollowUpFieldChange } = props;
    const dispatch = useDispatch();
    const dueDate = useSelector((state) => state.followUps.dueDate);
    const classes = Object.assign(useStyles(), useEntryStyle());
    const intl = useIntl();
    const [filteredReasons, setFilteredReasons] = useState(reasons);
    const { endToEndTypes } = useActivityQuestions();
    const isEndToEndEnabled = useIsEndToEndEnabled(props.workOrderNumber);
    const { checkDuplicateWorkOrder } = useCheckDuplicateWorkOrder();
    const NUMLENGTH = 20,
        COMMENTLENGTH = 300;
    const followUpKey = followUp.uuid || followUp.id;

    useEffect(() => {
        if (!isEndToEndEnabled) {
            setFilteredReasons(reasons.filter((reason) => !endToEndTypes.includes(reason.followUpTypeID)));
        }
    }, [isEndToEndEnabled, endToEndTypes, reasons]);

    useEffect(() => {
        if (endToEndTypes.includes(Number(getFollowUpTypeIdValue(followUp, reasons)))) {
            dispatch(requestActivityQuestions(followUp.workActivityID, followUp.uuid));
        }
    }, [followUp.followUpTpyeId, followUp.workActivityID, followUp.uuid, endToEndTypes, dispatch]);

    const update = (updates) => {
        handleFollowUpFieldChange(followUp.uuid, updates);
    };

    const getFollowUpIdValue = (followUp, reasons) => {
        if (followUp.followUpTpyeId) {
            if (followUp.followUpTpyeId.followUpTypeID) {
                return reasons.find((r) => r.followUpTypeID === followUp.followUpTpyeId.followUpTypeID);
            } else {
                return reasons.find((r) => r.followUpTypeID === followUp.followUpTpyeId);
            }
        }
    };

    const getFollowUpTypeIdValue = (followUp) => {
        if (followUp.followUpTpyeId && followUp.followUpTpyeId.followUpTypeID !== undefined) {
            return followUp.followUpTpyeId.followUpTypeID;
        } else {
            return followUp.followUpTpyeId;
        }
    };

    const getHasActivityQuestionsValue = (followUp) => {
        if (followUp.followUpTpyeId && followUp.followUpTpyeId.hasActivityQuestions !== undefined) {
            return followUp.followUpTpyeId.hasActivityQuestions;
        } else {
            return followUp.hasActivityQuestions;
        }
    };

    const handleFollowUpReasonChange = (_event, newValue) => {
        if (!newValue) return;
        const reason = filteredReasons.find((r) => r.followUpTypeID === newValue.followUpTypeID);
        const newDueDate = reason.newWorkOrderRequired
            ? dueDate
            : reason.leadTime
              ? moment(new Date()).businessAdd(reason.leadTime)._d
              : moment(new Date());
        const newFollowup = {
            followUpTpyeId: reason.followUpTypeID,
            workActivityID: reason.workActivityID,
            dirty: true,
            dueDate: newDueDate,
            disabled: false,
            newWorkOrderRequired: reason.newWorkOrderRequired,
            hasActivityQuestions: reason.hasActivityQuestions,
        };
        if (reason.newWorkOrderRequired) {
            checkDuplicateWorkOrder(newFollowup, [], "admin", false, {});
        }
        update(newFollowup);
    };

    let moment = require("moment-business-days");
    moment.updateLocale("us", { workingWeekdays: [1, 2, 3, 4, 5] });

    return (
        <Grid className={classes.entryContainer + " " + classes.entrySpacing} item xs={12}>
            <Grid container spacing={1} alignItems="center">
                <Grid item spacing={1} xs={12} sm container>
                    <Grid item lg={3} md={6} xs={12}>
                        <Caption>
                            <FormattedMessage
                                id="admin.work-order-details.follow-up-list.reason-col-header"
                                defaultMessage="Follow-up reason"
                                description="Admin work order follow-up reason column header"
                            />
                        </Caption>
                        <Autocomplete
                            key={`${followUp.uuid}-${intl.locale}`}
                            id="follow-up-reason-combo-box"
                            fullWidth
                            value={getFollowUpIdValue(followUp, reasons) || null}
                            closeText={intl.formatMessage(labels.close)}
                            openText={intl.formatMessage(labels.open)}
                            noOptionsText={intl.formatMessage(labels.noOptions)}
                            options={filteredReasons}
                            disabled={!isDisabled}
                            onChange={handleFollowUpReasonChange}
                            getOptionLabel={(option) => getLocalizedNameWithIntl(intl, { object: option, value: "name" })}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    fullWidth
                                    variant="outlined"
                                    error={followUp.followUpTpyeId === null}
                                    InputProps={{
                                        ...params.InputProps,
                                        style: { padding: "8px" },
                                    }}
                                    InputLabelProps={{
                                        style: { fontSize: "14px", maxWidth: "90%" },
                                    }}
                                />
                            )}
                        />
                    </Grid>

                    <Grid item lg={3} md={6} xs={12} className={classes.gridItem}>
                        <Caption>
                            <FormattedMessage
                                id="admin.work-order-details.follow-up-list.id-col-header"
                                defaultMessage="Reference Ticket Number"
                                description="Admin work order follow-up id column header"
                            />
                        </Caption>
                        <TextField
                            value={
                                reasons.find((r) => r.followUpTypeID === followUp.FK_FollowUpTpyeId) &&
                                reasons.find((r) => r.followUpTypeID === followUp.FK_FollowUpTpyeId).action &&
                                reasons.find((r) => r.followUpTypeID === followUp.FK_FollowUpTpyeId).action == "ACUT TICKET" &&
                                followUp.acutRelatedId
                                    ? followUp.acutRelatedId.acutTktNumber
                                    : followUp.referenceTicketNumber
                            }
                            required
                            fullWidth
                            variant="outlined"
                            error={followUp.id === null || followUp.id.toString().length <= 0}
                            onChange={(event) => update({ referenceTicketNumber: event.target.value })}
                            InputLabelProps={{
                                style: { fontSize: "14px", maxWidth: "90%" },
                            }}
                            disabled
                            InputProps={{
                                style: { padding: "8px" },
                            }}
                            style={{
                                background: "#F4F4F4",
                            }}
                        />

                        <div className={classes.characterCount}>
                            {followUp.id ? `${followUp.id.toString().length}/${NUMLENGTH}` : `0/${NUMLENGTH}`}
                        </div>
                    </Grid>
                    <Grid item lg={3} md={6} xs={12} className={classes.gridItem}>
                        <Caption>
                            <FormattedMessage
                                id="admin.work-order-details.follow-up-list.date-created-col-header"
                                defaultMessage="Date created"
                                description="Admin work order follow-up date created column header"
                            />
                        </Caption>
                        <TextField
                            fullWidth
                            variant="outlined"
                            value={
                                followUp.creationDate
                                    ? moment.utc(new Date(followUp.creationDate)).add(-moment().utcOffset(), "minutes").format("MMM DD, YYYY")
                                    : ""
                            }
                            error={null}
                            disabled
                            InputLabelProps={{
                                style: { fontSize: "14px", maxWidth: "90%" },
                            }}
                            InputProps={{
                                style: { padding: "8px" },
                            }}
                        />
                    </Grid>
                    <Grid item md className={classes.gridItem}>
                        <Caption>
                            <FormattedMessage
                                id="admin.work-order-details.follow-up-list.due-date-col-header"
                                defaultMessage="Due date"
                                description="Admin work order follow-up due date column header"
                            />
                        </Caption>

                        <TextField
                            fullWidth
                            variant="outlined"
                            value={
                                followUp.dueDate != ""
                                    ? moment.utc(new Date(followUp.dueDate)).add(-moment().utcOffset(), "minutes").format("MMM DD, YYYY")
                                    : ""
                            }
                            error={null}
                            disabled
                            InputLabelProps={{
                                style: { fontSize: "14px", maxWidth: "90%" },
                            }}
                            InputProps={{
                                style: { padding: "8px" },
                            }}
                        />
                    </Grid>
                    {getHasActivityQuestionsValue(followUp) && (
                        <Grid item xs={12} className={classes.gridItem}>
                            <FollowupActivityQuestions followUpUUID={followUpKey} />
                        </Grid>
                    )}

                    <Grid item xs={12} className={classes.gridItem}>
                        <Caption>
                            <FormattedMessage
                                id="admin.work-order-details.follow-up-list.comment-col-header"
                                defaultMessage="Comment"
                                description="Admin work order follow-up comment column header"
                            />
                        </Caption>
                        <TextField
                            className={classes.root}
                            variant="outlined"
                            InputProps={{
                                inputProps: {
                                    maxLength: COMMENTLENGTH,
                                },
                            }}
                            fullWidth
                            multiline
                            value={followUp.comments}
                            onChange={(event) => update({ comments: event.target.value })}
                            maxRows="4"
                            disabled={!isDisabled}
                        />
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );
};

export default FollowUpsRowEdit;
